import { Fragment, useContext } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { NextPrevButton } from 'Components';
import { InvoiceCard } from './InvoiceCard';
import { STEP } from '..';
import { UploadMoreInvoiceBtn } from 'Modules/Reimbursements/UploadMoreInvoiceBtn';
import { AlternativeMedicineContext } from 'Context/AlternativeMedicine';
import { BackButton } from '../BackButton';

export const InvoiceList = () => {
  const {
    formData: { invoices },
    deleteInvoice,
    editInvoice,
    currentStep,
    handleNextStep,
    setShowUploadFileArea,
    setSelfTreatment,
    handlePrevStep,
    setShowTreatmentTypeForm,
  } = useContext(AlternativeMedicineContext);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();

  const getInvoicesSum = () => {
    return invoices
      .reduce((total, invoice) => total + (invoice.invoiceTotal || 0), 0)
      .toLocaleString();
  };

  const groupedInvoices = invoices.reduce((acc, invoice) => {
    const type = invoice.treatmentTypeName || '';
    acc[type] = acc[type] || [];
    acc[type].push(invoice);
    return acc;
  }, {} as Record<string, typeof invoices>);

  return (
    <Stack sx={{ gap: '23px', maxWidth: '800px', m: '32px auto 0' }}>
      <Typography variant='h7' textAlign={isDesktop ? undefined : 'center'}>
        {dictionary.allInvoicesByType}
      </Typography>

      {Object.entries(groupedInvoices).map(([type, invoicesByType]) => (
        <Fragment key={type}>
          <Typography variant='body2' sx={{ mt: 2 }}>
            {type}
          </Typography>
          {(invoicesByType as typeof invoices).map((invoice, index) => (
            <InvoiceCard
              key={index}
              index={index}
              invoice={invoice}
              deleteInvoice={() => deleteInvoice(index)}
              editInvoice={() => editInvoice(index)}
            />
          ))}
        </Fragment>
      ))}

      <UploadMoreInvoiceBtn
        onClick={() => setShowTreatmentTypeForm(true)}
        formName={dictionary.typeAlternativeMedicine}
      />

      <Divider
        aria-hidden='true'
        sx={{
          m: isDesktop ? '64px 0 0' : '0',
          width: '100%',
          borderColor: 'primary.shade_100',
        }}
      />
      <Typography variant='body3'>
        {dictionary.totalAmount} <strong>{getInvoicesSum()}</strong>{' '}
        {dictionary.nis}
      </Typography>
      <Stack
        direction={isDesktop ? 'row-reverse' : 'column'}
        mt={3}
        gap={isDesktop ? '48px' : '20px'}
        justifyContent='center'
      >
        <NextPrevButton
          callback={() => {
            if (currentStep === STEP.INVOICE_UPLOAD) {
              handleNextStep();
            } else if (currentStep === STEP.ADDITIONAL_DETAILS) {
              handleNextStep();
            }
          }}
          text={dictionary.save}
          style={
            isDesktop
              ? undefined
              : {
                  button: { height: '36px', width: '100%' },
                  text: { fontSize: '16px', fontWeight: '600' },
                }
          }
          isBlue
          isNext
        />
        <BackButton isExitBtn />
      </Stack>
    </Stack>
  );
};
